import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import {
  dismissIdpRefresh,
  recentAttempt,
  recentlyActive,
  recordAttempt,
  refederateHref,
  refederateNavigation,
  useIdpRefreshDismissed,
  useIdpRefreshDue,
} from "./idp-refresh.ts";
import { MAIN_ID } from "./main-region.ts";
import { currentPath, useHasUnsavedWork, useSessionEnded } from "./session-ended.ts";

/**
 * Keeps a federated session's company groups fresh. When renewal is due and the tab is visible
 * with nothing unsaved, it passes through the IdP and back to this page (no prompt while the
 * IdP's own session lasts). With a draft, an upload or an edit in progress, or right after an
 * attempt that did not renew, it never leaves by itself: a quiet banner offers the refresh.
 */
const subscribeVisibility = (listener: () => void) => {
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
};
const isVisible = () => document.visibilityState !== "hidden";

export function IdpRefresh() {
  const { t } = useTranslation();
  const due = useIdpRefreshDue();
  const dismissed = useIdpRefreshDismissed();
  const unsaved = useHasUnsavedWork();
  // An ended session has its own banner and its own way back.
  const ended = useSessionEnded();
  // A renewal that fell due while the tab was hidden runs once it is visible again.
  const visible = useSyncExternalStore(subscribeVisibility, isVisible);
  const silent = due && !ended && !unsaved && !recentAttempt() && recentlyActive();

  useEffect(() => {
    if (silent && visible) {
      recordAttempt();
      refederateNavigation.go(refederateHref(currentPath()));
    }
  }, [silent, visible]);

  if (!due || ended || dismissed || silent) {
    return null;
  }
  const later = () => {
    dismissIdpRefresh();
    // The focused button is gone: continue from the page content, not the document start.
    document.getElementById(MAIN_ID)?.focus();
  };
  return (
    <div className="shrink-0 border-b border-line px-4 py-2 sm:px-6">
      <Notice
        tone="info"
        live
        action={
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <a href={refederateHref(currentPath())} onClick={() => recordAttempt()}>
                {t("session.idpRefresh.action")}
              </a>
            </Button>
            <Button size="sm" variant="ghost" onClick={later}>
              {t("session.idpRefresh.later")}
            </Button>
          </div>
        }
      >
        {t("session.idpRefresh.lead")} {unsaved ? t("session.idpRefresh.unsaved") : null}
      </Notice>
    </div>
  );
}
